import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAccountsPipeline } from '@/lib/useAccountsPipeline';
import { useAuth } from '@/lib/auth';
import {
  championStrengthMeta,
  computeHealth,
  fmtCurrency,
  fmtRelative,
  healthBand,
  isStalled,
  missingFields,
  msaMeta,
  stageMeta,
  vendorMeta,
  type PipelineAccount,
  type StageKey,
} from '@/lib/pipelineAdapter';
import {
  Avatar,
  Chip,
  HealthRing,
  Logo,
  StageChip,
} from '@/components/smartTable/atoms';
import { SyncPipelineButton } from '@/components/SyncPipelineButton';

type ViewKey = 'all' | 'mine' | 'risk' | 'closing' | 'stalled' | 'missing-eb';
type SortKey = 'health' | 'arr' | 'momentum' | 'activity';

interface ViewDef {
  id: ViewKey;
  label: string;
  filter: (a: PipelineAccount, callerEmail: string | null) => boolean;
}

const VIEWS: ViewDef[] = [
  { id: 'all', label: 'All', filter: () => true },
  {
    id: 'mine',
    label: 'My deals',
    filter: (a, email) =>
      !!email && (a.owner.email ?? '').toLowerCase() === email,
  },
  { id: 'risk', label: 'At risk', filter: (a) => computeHealth(a) < 50 },
  {
    id: 'closing',
    label: 'Closing this Q',
    filter: (a) => a.stage === 'negotiation' || a.stage === 'closing',
  },
  { id: 'stalled', label: 'Stalled', filter: (a) => isStalled(a) },
  {
    id: 'missing-eb',
    label: 'Missing EB',
    filter: (a) => missingFields(a).includes('EB'),
  },
];

export function PipelinePage() {
  const { user } = useAuth();
  const { accounts, loading } = useAccountsPipeline();
  const callerEmail = user?.email?.toLowerCase() ?? null;
  const [searchParams] = useSearchParams();

  const initialView = searchParams.get('view');
  const initialStage = searchParams.get('stage');
  const initialOwner = searchParams.get('owner');

  const [view, setView] = useState<ViewKey>(() => {
    if (
      initialView === 'mine' ||
      initialView === 'risk' ||
      initialView === 'closing' ||
      initialView === 'stalled' ||
      initialView === 'missing-eb' ||
      initialView === 'all'
    ) {
      return initialView;
    }
    return 'all';
  });
  const [stageFilter, setStageFilter] = useState<StageKey | null>(() => {
    if (
      initialStage === 'discovery' ||
      initialStage === 'evaluation' ||
      initialStage === 'negotiation' ||
      initialStage === 'closing'
    ) {
      return initialStage;
    }
    return null;
  });
  const [ownerFilter, setOwnerFilter] = useState<string | null>(() =>
    initialOwner ? initialOwner.toLowerCase() : null,
  );
  const [sort, setSort] = useState<SortKey>('health');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const counts = useMemo(() => {
    const out: Record<ViewKey, number> = {
      all: 0,
      mine: 0,
      risk: 0,
      closing: 0,
      stalled: 0,
      'missing-eb': 0,
    };
    for (const v of VIEWS) {
      out[v.id] = accounts.filter((a) => v.filter(a, callerEmail)).length;
    }
    return out;
  }, [accounts, callerEmail]);

  const filtered = useMemo(() => {
    const def = VIEWS.find((v) => v.id === view) ?? VIEWS[0];
    let r = accounts.filter((a) => def.filter(a, callerEmail));
    if (stageFilter) {
      r = r.filter((a) => a.stage === stageFilter);
    }
    if (ownerFilter) {
      r = r.filter(
        (a) => (a.owner.email ?? '').toLowerCase() === ownerFilter,
      );
    }
    const q = search.trim().toLowerCase();
    if (q) {
      r = r.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          (a.economicBuyer.value ?? '').toLowerCase().includes(q) ||
          (a.champion.name ?? '').toLowerCase().includes(q),
      );
    }
    return [...r].sort((a, b) => {
      if (sort === 'health') return computeHealth(b) - computeHealth(a);
      if (sort === 'arr') return (b.arr ?? 0) - (a.arr ?? 0);
      if (sort === 'momentum') return (b.momentum ?? 0) - (a.momentum ?? 0);
      if (sort === 'activity') {
        const at = a.lastActivity?.getTime() ?? 0;
        const bt = b.lastActivity?.getTime() ?? 0;
        return bt - at;
      }
      return 0;
    });
  }, [accounts, view, search, sort, callerEmail, stageFilter, ownerFilter]);

  const owners = useMemo(() => {
    const map = new Map<
      string,
      { name: string; initials: string; color: string; count: number }
    >();
    for (const a of accounts) {
      const cur = map.get(a.owner.name);
      if (cur) cur.count += 1;
      else
        map.set(a.owner.name, {
          name: a.owner.name,
          initials: a.owner.initials,
          color: a.owner.color,
          count: 1,
        });
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [accounts]);

  const selected =
    accounts.find((a) => a.id === selectedId) ?? filtered[0] ?? null;
  const activeView = VIEWS.find((v) => v.id === view) ?? VIEWS[0];

  return (
    <div
      style={{
        background: '#f7f8fa',
        borderRadius: 10,
        border: '1px solid #e8ebf0',
        overflow: 'hidden',
        height: 'calc(100vh - 130px)',
        minHeight: 600,
        display: 'flex',
        fontFamily:
          '"Inter", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
      }}
    >
      <SavedViewsSidebar
        view={view}
        onChange={setView}
        counts={counts}
        owners={owners}
      />

      <ListPane
        label={activeView.label}
        accounts={filtered}
        loading={loading}
        totalAccounts={accounts.length}
        selectedId={selected?.id ?? null}
        onSelect={(id) => setSelectedId(id)}
        search={search}
        onSearch={setSearch}
        sort={sort}
        onSort={setSort}
        stageFilter={stageFilter}
        ownerFilter={ownerFilter}
        onClearStageFilter={() => setStageFilter(null)}
        onClearOwnerFilter={() => setOwnerFilter(null)}
      />

      <section
        style={{
          flex: 1,
          minWidth: 0,
          overflowY: 'auto',
          background: '#f7f8fa',
        }}
      >
        {selected ? (
          <DetailView account={selected} />
        ) : loading ? (
          <EmptyState
            title="Loading pipeline…"
            body="Streaming accounts from Firestore."
          />
        ) : (
          <EmptyState
            title="No accounts yet"
            body="Run the Salesforce cohort sync to pull opportunities and ingest recent Gong calls."
            cta={<SyncPipelineButton />}
          />
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar — saved views + owners
// ---------------------------------------------------------------------------

function SavedViewsSidebar({
  view,
  onChange,
  counts,
  owners,
}: {
  view: ViewKey;
  onChange: (v: ViewKey) => void;
  counts: Record<ViewKey, number>;
  owners: { name: string; initials: string; color: string; count: number }[];
}) {
  return (
    <aside
      style={{
        width: 200,
        padding: '18px 12px',
        borderRight: '1px solid #e8ebf0',
        background: 'white',
        flexShrink: 0,
        overflowY: 'auto',
      }}
    >
      <SectionHeader>Saved views</SectionHeader>
      {VIEWS.map((v) => {
        const active = view === v.id;
        return (
          <button
            key={v.id}
            onClick={() => onChange(v.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              textAlign: 'left',
              padding: '7px 10px',
              fontSize: 13,
              fontWeight: 500,
              border: 'none',
              cursor: 'pointer',
              borderRadius: 6,
              marginBottom: 2,
              background: active ? '#eef2ff' : 'transparent',
              color: active ? '#3730a3' : '#475569',
            }}
          >
            <span style={{ flex: 1 }}>{v.label}</span>
            <span
              style={{
                fontSize: 11,
                color: active ? '#3730a3' : '#94a3b8',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {counts[v.id]}
            </span>
          </button>
        );
      })}

      {owners.length > 0 && (
        <>
          <div style={{ height: 12 }} />
          <SectionHeader>Owners</SectionHeader>
          {owners.map((o) => (
            <div
              key={o.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                fontSize: 12,
                color: '#475569',
                borderRadius: 6,
              }}
            >
              <Avatar
                name={o.name}
                initials={o.initials}
                color={o.color}
                size={18}
              />
              <span
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {o.name}
              </span>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>{o.count}</span>
            </div>
          ))}
        </>
      )}
    </aside>
  );
}

function SectionHeader({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.6,
        color: '#94a3b8',
        textTransform: 'uppercase',
        padding: '0 8px 8px',
      }}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Middle list pane
// ---------------------------------------------------------------------------

function ListPane({
  label,
  accounts,
  loading,
  totalAccounts,
  selectedId,
  onSelect,
  search,
  onSearch,
  sort,
  onSort,
  stageFilter,
  ownerFilter,
  onClearStageFilter,
  onClearOwnerFilter,
}: {
  label: string;
  accounts: PipelineAccount[];
  loading: boolean;
  totalAccounts: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
  search: string;
  onSearch: (q: string) => void;
  sort: SortKey;
  onSort: (s: SortKey) => void;
  stageFilter: StageKey | null;
  ownerFilter: string | null;
  onClearStageFilter: () => void;
  onClearOwnerFilter: () => void;
}) {
  return (
    <section
      style={{
        width: 360,
        borderRight: '1px solid #e8ebf0',
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          padding: '14px 14px 10px',
          borderBottom: '1px solid #e8ebf0',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 8,
          }}
        >
          <span
            style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}
          >
            {label}
          </span>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>
            {accounts.length}
          </span>
        </div>
        {(stageFilter || ownerFilter) && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginBottom: 8,
            }}
          >
            {stageFilter && (
              <FilterChip
                label={`Stage: ${stageMeta[stageFilter].label}`}
                onClear={onClearStageFilter}
              />
            )}
            {ownerFilter && (
              <FilterChip label={`Owner: ${ownerFilter}`} onClear={onClearOwnerFilter} />
            )}
          </div>
        )}
        <input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search…"
          style={{
            width: '100%',
            padding: '6px 10px 6px 28px',
            fontSize: 12,
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            outline: 'none',
            background: `white url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'><circle cx='11' cy='11' r='7'/><path d='m20 20-3.5-3.5'/></svg>") no-repeat 9px 50%`,
            boxSizing: 'border-box',
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 8,
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: '#94a3b8',
              letterSpacing: 0.5,
              textTransform: 'uppercase',
            }}
          >
            Sort
          </span>
          <select
            value={sort}
            onChange={(e) => onSort(e.target.value as SortKey)}
            style={{
              fontSize: 11,
              padding: '3px 6px',
              border: '1px solid #e2e8f0',
              borderRadius: 4,
              background: 'white',
              color: '#475569',
              cursor: 'pointer',
            }}
          >
            <option value="health">Health</option>
            <option value="arr">ARR</option>
            <option value="momentum">Momentum</option>
            <option value="activity">Last activity</option>
          </select>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading && totalAccounts === 0 && (
          <div
            style={{
              padding: 30,
              textAlign: 'center',
              fontSize: 12,
              color: '#94a3b8',
            }}
          >
            Loading…
          </div>
        )}
        {!loading && accounts.length === 0 && (
          <div
            style={{
              padding: 30,
              textAlign: 'center',
              fontSize: 12,
              color: '#94a3b8',
            }}
          >
            No accounts match.
          </div>
        )}
        {accounts.map((a) => (
          <ListItem
            key={a.id}
            a={a}
            selected={a.id === selectedId}
            onClick={() => onSelect(a.id)}
          />
        ))}
      </div>
    </section>
  );
}

function FilterChip({
  label,
  onClear,
}: {
  label: string;
  onClear: () => void;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        padding: '3px 8px',
        borderRadius: 999,
        background: '#eef2ff',
        color: '#3730a3',
      }}
    >
      {label}
      <button
        type="button"
        onClick={onClear}
        style={{
          border: 'none',
          background: 'transparent',
          color: '#64748b',
          cursor: 'pointer',
          fontSize: 12,
          lineHeight: 1,
          padding: 0,
        }}
        aria-label={`Clear ${label}`}
      >
        ×
      </button>
    </span>
  );
}

function ListItem({
  a,
  selected,
  onClick,
}: {
  a: PipelineAccount;
  selected: boolean;
  onClick: () => void;
}) {
  const score = computeHealth(a);
  const band = healthBand(score);
  const stage = stageMeta[a.stage];
  const stalled = isStalled(a);
  return (
    <div
      onClick={onClick}
      style={{
        padding: '12px 14px',
        cursor: 'pointer',
        borderBottom: '1px solid #f1f5f9',
        borderLeft: selected ? '2px solid #3730a3' : '2px solid transparent',
        background: selected ? '#eef2ff' : 'transparent',
        transition: 'background .12s',
      }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.background = '#fafbfc';
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.background = 'transparent';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <Logo color={a.logoColor} label={a.logo} size={28} radius={6} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 2,
            }}
          >
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#0f172a',
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {a.name}
            </span>
            <span
              style={{
                fontSize: 11,
                color: band.color,
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {a.hasAnalysis ? score : '—'}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11,
              color: '#64748b',
              marginBottom: 4,
            }}
          >
            <span style={{ color: stage.color, fontWeight: 500 }}>
              {stage.label}
            </span>
            <span>·</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {fmtCurrency(a.arr)}
            </span>
            <span>·</span>
            <span style={{ color: stalled ? '#b91c1c' : '#94a3b8' }}>
              {fmtRelative(a.lastActivity)}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <SignalMini label="M" value={a.momentum} color="#3730a3" />
            <SignalMini label="S" value={a.strength} color="#0891b2" />
            {a.blockers.length > 0 && (
              <span
                style={{
                  fontSize: 10,
                  color: '#b91c1c',
                  background: '#fef2f2',
                  padding: '1px 5px',
                  borderRadius: 999,
                  fontWeight: 600,
                }}
              >
                {a.blockers.length} blocker
                {a.blockers.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        <Avatar
          name={a.owner.name}
          initials={a.owner.initials}
          color={a.owner.color}
          size={20}
        />
      </div>
    </div>
  );
}

function SignalMini({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      <span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 700 }}>
        {label}
      </span>
      <span style={{ display: 'inline-flex', gap: 1.5 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <span
            key={i}
            style={{
              width: 6,
              height: 3,
              borderRadius: 1,
              background: i < value ? color : '#e2e8f0',
            }}
          />
        ))}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Detail pane
// ---------------------------------------------------------------------------

function DetailView({ account: a }: { account: PipelineAccount }) {
  const score = computeHealth(a);
  const band = healthBand(score);

  return (
    <div style={{ padding: 24, maxWidth: 920, margin: '0 auto' }}>
      <DetailHeader account={a} score={score} band={band} />

      <Card title="Deal qualification">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
            background: '#f1f5f9',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <ChecklistRow
            label="Economic Buyer"
            value={a.economicBuyer.value ?? 'Not identified'}
            sub={a.economicBuyer.confidence}
            ok={a.economicBuyer.confidence === 'confirmed'}
            warn={a.economicBuyer.confidence === 'tentative'}
          />
          <ChecklistRow
            label="EB signed off"
            value={
              a.ebSignedOff === 'yes'
                ? 'Yes'
                : a.ebSignedOff === 'partial'
                  ? 'Partial'
                  : 'No'
            }
            ok={a.ebSignedOff === 'yes'}
            warn={a.ebSignedOff === 'partial'}
          />
          <ChecklistRow
            label="Champion"
            value={a.champion.name ?? 'Not identified'}
            sub={a.champion.role ?? undefined}
            ok={!!a.champion.name && a.champion.strength === 'strong'}
            warn={!!a.champion.name && a.champion.strength === 'medium'}
          />
          <ChecklistRow
            label="Budget approved"
            value={
              a.budgetApproved.value === 'yes'
                ? 'Yes'
                : a.budgetApproved.value === 'partial'
                  ? 'Partial'
                  : 'No'
            }
            sub={a.budgetApproved.note ?? undefined}
            ok={a.budgetApproved.value === 'yes'}
            warn={a.budgetApproved.value === 'partial'}
          />
          <ChecklistRow
            label="Launch date agreed"
            value={
              a.launchDateAgreed.value
                ? new Date(a.launchDateAgreed.value).toLocaleDateString(
                    'en-US',
                    { month: 'short', day: 'numeric', year: 'numeric' },
                  )
                : 'Unset'
            }
            sub={a.launchDateAgreed.status}
            ok={a.launchDateAgreed.status === 'agreed'}
            warn={a.launchDateAgreed.status === 'tentative'}
          />
          <ChecklistRow
            label="Use case"
            value={a.useCase ?? 'No use case captured'}
            ok={!!a.useCase}
            multiline
          />
        </div>
      </Card>

      <Card title="Use case (from latest call)">
        <div
          style={{
            padding: '12px 14px',
            background: '#fafbfc',
            borderRadius: 8,
            fontSize: 13,
            color: '#334155',
            lineHeight: 1.55,
          }}
        >
          {a.useCase || (
            <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>
              No use case captured
            </span>
          )}
        </div>
      </Card>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
        }}
      >
        <Card
          title={`Blockers · ${a.blockers.length}`}
          headerAccent="#b91c1c"
        >
          {a.blockers.length === 0 ? (
            <div
              style={{
                padding: '14px 0',
                fontSize: 13,
                color: '#94a3b8',
                fontStyle: 'italic',
              }}
            >
              No blockers — clear runway
            </div>
          ) : (
            a.blockers.map((b, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 10,
                  padding: '10px 0',
                  borderBottom:
                    i < a.blockers.length - 1
                      ? '1px solid #f1f5f9'
                      : 'none',
                }}
              >
                <span
                  style={{
                    width: 16,
                    height: 16,
                    border: '1.5px solid #cbd5e1',
                    borderRadius: 4,
                    background: 'white',
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13,
                      color: '#334155',
                      lineHeight: 1.5,
                    }}
                  >
                    {b}
                  </div>
                </div>
              </div>
            ))
          )}
        </Card>

        <Card
          title={`Next steps · ${a.nextSteps.length}`}
          headerAccent="#3730a3"
        >
          {a.nextSteps.length === 0 ? (
            <div
              style={{
                padding: '14px 0',
                fontSize: 13,
                color: '#94a3b8',
                fontStyle: 'italic',
              }}
            >
              No next steps captured
            </div>
          ) : (
            a.nextSteps.map((s, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 10,
                  padding: '10px 0',
                  borderBottom:
                    i < a.nextSteps.length - 1
                      ? '1px solid #f1f5f9'
                      : 'none',
                }}
              >
                <span
                  style={{
                    width: 16,
                    height: 16,
                    border: '1.5px solid #cbd5e1',
                    borderRadius: 4,
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13,
                      color: '#334155',
                      lineHeight: 1.5,
                    }}
                  >
                    {s}
                  </div>
                </div>
              </div>
            ))
          )}
        </Card>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
        }}
      >
        <Card title="People">
          <PersonRow
            label="Champion"
            name={a.champion.name}
            role={a.champion.role}
            strength={a.champion.strength}
          />
          <PersonRow
            label="Economic Buyer"
            name={a.economicBuyer.value}
            confidence={a.economicBuyer.confidence}
          />
          <PersonRow
            label="Legal"
            name={a.legalContact.name}
            status={a.legalContact.name ? 'engaged' : 'missing'}
          />
          <PersonRow
            label="Procurement"
            name={a.procurementContact.name}
            status={a.procurementContact.name ? 'engaged' : 'missing'}
            last
          />
        </Card>

        <Card title="Paperwork">
          <PaperRow label="MSA / Terms" status={a.msaTerms} kind="msa" />
          <PaperRow
            label="Vendor registration"
            status={a.vendorReg}
            kind="vendor"
            last
          />
        </Card>
      </div>

      <Card title="Recent activity">
        <ActivitySummary account={a} />
      </Card>
    </div>
  );
}

function DetailHeader({
  account: a,
  score,
  band,
}: {
  account: PipelineAccount;
  score: number;
  band: ReturnType<typeof healthBand>;
}) {
  return (
    <div
      style={{
        background: 'white',
        borderRadius: 12,
        border: '1px solid #e8ebf0',
        padding: 20,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
          marginBottom: 16,
        }}
      >
        <Logo color={a.logoColor} label={a.logo} size={48} radius={10} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 4,
              flexWrap: 'wrap',
            }}
          >
            <StageChip stage={a.stage} />
            <span style={{ fontSize: 12, color: '#64748b' }}>
              · {fmtCurrency(a.arr)} ARR · {a.calls} call
              {a.calls === 1 ? '' : 's'}
              {a.emails > 0 ? ` · ${a.emails} emails` : ''} · last{' '}
              {fmtRelative(a.lastActivity)}
            </span>
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: '#0f172a',
              letterSpacing: -0.3,
            }}
          >
            {a.name}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <Link
            to={`/accounts/${a.id}`}
            style={{
              padding: '7px 12px',
              fontSize: 12,
              fontWeight: 500,
              color: '#475569',
              border: '1px solid #e2e8f0',
              background: 'white',
              borderRadius: 6,
              cursor: 'pointer',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            Open account
          </Link>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '14px 16px',
          background: a.hasAnalysis ? band.bg : '#f8fafc',
          borderRadius: 10,
        }}
      >
        {a.hasAnalysis ? (
          <HealthRing score={score} size={48} stroke={4} />
        ) : (
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 999,
              border: '4px solid #e2e8f0',
            }}
          />
        )}
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 11,
              color: '#64748b',
              marginBottom: 1,
              fontWeight: 500,
            }}
          >
            Deal health
          </div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: a.hasAnalysis ? band.color : '#94a3b8',
            }}
          >
            {a.hasAnalysis ? band.label : 'Not analyzed yet'}
          </div>
        </div>
        <SignalLarge label="Momentum" value={a.momentum} color="#3730a3" />
        <SignalLarge label="Strength" value={a.strength} color="#0891b2" />
        <div style={{ width: 1, height: 32, background: '#e2e8f0' }} />
        <div>
          <div
            style={{
              fontSize: 10,
              color: '#64748b',
              textTransform: 'uppercase',
              fontWeight: 600,
              letterSpacing: 0.4,
            }}
          >
            Owner
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 2,
            }}
          >
            <Avatar
              name={a.owner.name}
              initials={a.owner.initials}
              color={a.owner.color}
              size={20}
            />
            <span
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: '#0f172a',
              }}
            >
              {a.owner.name}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SignalLarge({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          color: '#64748b',
          textTransform: 'uppercase',
          fontWeight: 600,
          letterSpacing: 0.4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginTop: 4,
        }}
      >
        <span style={{ display: 'inline-flex', gap: 3 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <span
              key={i}
              style={{
                width: 16,
                height: 5,
                borderRadius: 2,
                background: i < value ? color : '#e2e8f0',
              }}
            />
          ))}
        </span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: '#0f172a',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}/5
        </span>
      </div>
    </div>
  );
}

function Card({
  title,
  children,
  headerAccent,
}: {
  title: ReactNode;
  children: ReactNode;
  headerAccent?: string;
}) {
  return (
    <div
      style={{
        background: 'white',
        borderRadius: 12,
        border: '1px solid #e8ebf0',
        padding: 16,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.5,
          color: headerAccent || '#94a3b8',
          textTransform: 'uppercase',
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function ChecklistRow({
  label,
  value,
  sub,
  ok,
  warn,
  multiline,
}: {
  label: string;
  value: string;
  sub?: string;
  ok?: boolean;
  warn?: boolean;
  multiline?: boolean;
}) {
  const color = ok ? '#10b981' : warn ? '#eab308' : '#94a3b8';
  const lineClampStyle: CSSProperties = multiline
    ? {}
    : {
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      };
  return (
    <div
      style={{
        background: 'white',
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
      }}
    >
      <span
        style={{
          width: 16,
          height: 16,
          borderRadius: 999,
          background: color,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: 10,
          fontWeight: 700,
          marginTop: 2,
        }}
      >
        {ok ? '✓' : warn ? '!' : '–'}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            color: '#64748b',
            fontWeight: 500,
            marginBottom: 2,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 13,
            color: '#0f172a',
            lineHeight: 1.4,
            ...lineClampStyle,
          }}
        >
          {value}
        </div>
        {sub && (
          <div
            style={{
              fontSize: 11,
              color: '#94a3b8',
              marginTop: 2,
              textTransform: 'capitalize',
            }}
          >
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

function PersonRow({
  label,
  name,
  role,
  strength,
  confidence,
  status,
  last,
}: {
  label: string;
  name?: string | null;
  role?: string | null;
  strength?: PipelineAccount['champion']['strength'];
  confidence?: PipelineAccount['economicBuyer']['confidence'];
  status?: 'engaged' | 'missing';
  last?: boolean;
}) {
  const meta = strength ? championStrengthMeta[strength] : null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 0',
        borderBottom: last ? 'none' : '1px solid #f1f5f9',
      }}
    >
      <span
        style={{
          fontSize: 11,
          color: '#94a3b8',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          width: 100,
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      {name ? (
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              color: '#0f172a',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </div>
          {role && (
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
              {role}
            </div>
          )}
        </div>
      ) : (
        <span
          style={{
            flex: 1,
            fontSize: 12,
            color: '#94a3b8',
            fontStyle: 'italic',
          }}
        >
          Not identified
        </span>
      )}
      {meta && name && (
        <Chip size="sm" bg={meta.bg} color={meta.fg} dot={meta.dot}>
          {meta.label}
        </Chip>
      )}
      {confidence && name && (
        <Chip size="sm" bg="#f1f5f9" color="#475569">
          {confidence}
        </Chip>
      )}
      {status === 'engaged' && (
        <Chip size="sm" bg="#ecfdf5" color="#047857" dot="#10b981">
          engaged
        </Chip>
      )}
      {status === 'missing' && (
        <Chip size="sm" bg="#fef2f2" color="#b91c1c">
          missing
        </Chip>
      )}
    </div>
  );
}

function PaperRow({
  label,
  status,
  kind,
  last,
}: {
  label: string;
  status: PipelineAccount['msaTerms'] | PipelineAccount['vendorReg'];
  kind: 'msa' | 'vendor';
  last?: boolean;
}) {
  const m =
    kind === 'msa'
      ? msaMeta[status as PipelineAccount['msaTerms']]
      : vendorMeta[status as PipelineAccount['vendorReg']];
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 0',
        borderBottom: last ? 'none' : '1px solid #f1f5f9',
      }}
    >
      <span
        style={{
          fontSize: 11,
          color: '#94a3b8',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          flex: 1,
        }}
      >
        {label}
      </span>
      <Chip size="sm" bg={m.bg} color={m.fg}>
        {m.label}
      </Chip>
    </div>
  );
}

function ActivitySummary({ account: a }: { account: PipelineAccount }) {
  const items: { label: string; value: string; accent?: string }[] = [
    {
      label: 'Last call',
      value: a.lastActivity
        ? `${fmtRelative(a.lastActivity)} (${a.lastActivity.toLocaleDateString()})`
        : 'No calls ingested',
      accent: isStalled(a) ? '#b91c1c' : undefined,
    },
    {
      label: 'Calls analyzed',
      value: String(a.calls),
    },
    {
      label: 'SF emails',
      value: String(a.emails),
    },
    {
      label: 'Last updated',
      value: a.updatedAt
        ? `${fmtRelative(a.updatedAt)} (${a.updatedAt.toLocaleDateString()})`
        : '—',
    },
  ];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 12,
      }}
    >
      {items.map((it) => (
        <div
          key={it.label}
          style={{
            padding: '10px 12px',
            background: '#fafbfc',
            borderRadius: 8,
            border: '1px solid #f1f5f9',
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: '#94a3b8',
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            {it.label}
          </div>
          <div
            style={{
              fontSize: 13,
              color: it.accent ?? '#0f172a',
              fontWeight: 500,
            }}
          >
            {it.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({
  title,
  body,
  cta,
}: {
  title: string;
  body: string;
  cta?: ReactNode;
}) {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
      }}
    >
      <div style={{ maxWidth: 400, textAlign: 'center' }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: '#0f172a',
            marginBottom: 6,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 13,
            color: '#64748b',
            lineHeight: 1.5,
            marginBottom: cta ? 16 : 0,
          }}
        >
          {body}
        </div>
        {cta && <div>{cta}</div>}
      </div>
    </div>
  );
}
